import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import { useColumnBoardQuery } from '../../api/taskColumnsApi';
import { istDayKey } from '../../utils/dates';
import AnchorDateBar from './AnchorDateBar';
import ColumnCard from './ColumnCard';
import ColumnBuilderModal from './ColumnBuilderModal';
import ColumnMatchesModal from './ColumnMatchesModal';
import BoardEmptyState from './BoardEmptyState';
import TaskComposerModal from './TaskComposerModal';
import { useColumnActions } from './useColumnActions';
import './tasks.css';

/**
 * Today's Tasks — a saved-query board.
 *
 * The clinic defines the columns; each is a rule answering "who needs contacting, as of a date".
 * The page owns only the anchor date and which modal is open: every column's meaning lives on the
 * server, so the board itself stays thin no matter how many rule kinds exist.
 */
export default function TaskBoardPage() {
  const [anchor, setAnchor] = useState(() => istDayKey(new Date()));
  const [editing, setEditing] = useState(null);      // null | {} (new) | column (edit)
  const [showingAll, setShowingAll] = useState(null);
  const [composingTask, setComposingTask] = useState(false);

  const { data, isFetching, isLoading, refetch } = useColumnBoardQuery({ anchor, rowsPerColumn: 25 });
  const columns = data?.columns ?? [];
  const actions = useColumnActions();

  return (
    <>
      <div className="page-header">
        <div className="breadcrumb">
          <span>Home</span><span className="bc-sep">/</span><span className="bc-cur">Today's Tasks</span>
        </div>
        <div className="page-header-row">
          <div>
            <h1>Today's Tasks</h1>
            <div className="page-header-sub">
              Your columns, answered for one date. Change the date to see the same questions for another day.
            </div>
          </div>
          <button className="btn btn-primary" onClick={() => setEditing({})}>
            <Plus size={15} /> New column
          </button>
        </div>
      </div>

      <AnchorDateBar
        anchor={anchor}
        setAnchor={setAnchor}
        isFetching={isFetching}
        onRefresh={refetch}
      />

      {isLoading && <div className="skeleton" style={{ height: 220 }} />}

      {!isLoading && columns.length === 0 && (
        <BoardEmptyState onAddColumn={() => setEditing({})} />
      )}

      {columns.length > 0 && (
        <div className="kanban-board">
          {columns.map((column, i) => (
            <ColumnCard
              key={column.id}
              column={column}
              actions={actions}
              isFirst={i === 0}
              isLast={i === columns.length - 1}
              onEdit={(c) => setEditing(c)}
              onDelete={actions.remove}
              onMove={(c, delta) => actions.move(columns, c, delta)}
              onShowAll={(c) => setShowingAll(c)}
              onSendMessage={(c) => setShowingAll({ ...c, sendMode: true })}
              onAddTask={() => setComposingTask(true)}
            />
          ))}
        </div>
      )}

      {editing && (
        <ColumnBuilderModal
          column={editing.id ? editing : null}
          onClose={() => setEditing(null)}
        />
      )}

      {composingTask && (
        <TaskComposerModal task={null} onClose={() => setComposingTask(false)} />
      )}

      {showingAll && (
        <ColumnMatchesModal
          column={showingAll}
          anchor={anchor}
          actions={actions}
          onClose={() => setShowingAll(null)}
        />
      )}
    </>
  );
}
